import { Camera } from '@phosphor-icons/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { users as usersApi } from '../../api/endpoints';
import { BrandScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { TextArea } from '../../components/ui/Field';
import { PhotoPicker } from '../../components/ui/PhotoPicker';
import { useSession } from '../../store/SessionContext';
import { SiroccoMark } from '../../theme/illustrations';
import { t } from '../../i18n';

/**
 * « Personnalise ton profil », entre l'inscription et le choix du serveur.
 *
 * Un visage et deux lignes changent tout ce qui suit : on ecrit plus
 * volontiers a quelqu'un qu'a une silhouette grise, et le premier ecran
 * qu'un nouveau membre verra est une liste de gens. Le demander ici, une
 * fois, coute moins qu'un rappel repete plus tard.
 *
 * Mais rien n'est exige : aucun champ n'est obligatoire, et « Continuer »
 * passe aussi bien un ecran rempli qu'un ecran vide. Un compte n'a pas a
 * etre negocie, et tout se retrouve dans les reglages.
 *
 * Il y avait un « Je le ferai plus tard » sous le bouton. Il menait
 * exactement au meme endroit, et deux boutons qui font la meme chose ne
 * donnent pas un choix : ils en font douter.
 */
export function PersonalizeScreen() {
  const { user, setUser } = useSession();
  const navigate = useNavigate();

  const [bio, setBio] = useState(user?.bio ?? '');
  const [erreur, setErreur] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const suivant = () => navigate('/choose-spot', { replace: true });

  /*
    La photo part des qu'elle est choisie, sans attendre le bouton : on la
    voit alors a l'ecran, dans le rond, et c'est cette confirmation-la
    qu'on cherche quand on vient d'en choisir une.
  */
  async function poserPhoto(avatarUrl: string | null) {
    setErreur(null);
    try {
      setUser(await usersApi.update({ avatarUrl }));
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : t.auth.personnaliser.photoImpossible);
    }
  }

  async function continuer() {
    const texte = bio.trim();
    // Rien a dire de plus que la photo, deja partie : on passe.
    if (!texte || texte === (user?.bio ?? '')) return suivant();

    setPending(true);
    setErreur(null);
    try {
      setUser(await usersApi.update({ bio: texte }));
      suivant();
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : t.auth.personnaliser.impossible);
      setPending(false);
    }
  }

  return (
    <BrandScreen>
      <div className="spot">
        <div className="spot__logo">
          <SiroccoMark size={34} />
        </div>

        <h1 className="spot__title">{t.auth.personnaliser.titre}</h1>

        <div className="perso">
          <PhotoPicker
            preset="avatar"
            label={t.auth.personnaliser.choisirPhoto}
            className="perso__photo"
            onPicked={poserPhoto}
            onError={setErreur}
          >
            <Avatar user={user ?? undefined} size={116} ring />
            <span className="perso__badge" aria-hidden="true">
              <Camera size={20} weight="fill" />
            </span>
          </PhotoPicker>

          <TextArea
            tone="brand"
            className="perso__bio"
            label={t.auth.personnaliser.bio}
            placeholder={t.auth.personnaliser.bioExemple}
            value={bio}
            maxLength={160}
            onChange={(event) => setBio(event.target.value)}
          />
        </div>

        <div className="spot__footer">
          {erreur && <p className="spot__error">{erreur}</p>}

          <Button
            variant="brand"
            className="spot__cta"
            loading={pending}
            onClick={() => void continuer()}
          >
            {t.auth.personnaliser.continuer}
          </Button>
        </div>
      </div>
    </BrandScreen>
  );
}
